import * as z from 'zod/v4';

/**
 * To:
 *
 * 1. Identify the required environment variables for deploying a new environment:
 *    - Include `PublicEnvVars` in the list of public environment variables.
 *    - Incorporate `SecretEnvVars` in the list of secret environment variables.
 *
 * 2. Create a new environment variable for use in the code:
 *    - Update the relevant lists of environment variables (public, secret, etc.).
 *    - If necessary, modify `build_and_deploy.yaml` to inject the new environment variable.
 *    - Ensure the existing environment is updated accordingly.
 */

/**
 * List of all public env vars to defined on each deployed environments
 */
const PublicEnvVarsSchema = z.object({
  ENV: z.string().optional().default('production'),
  NODE_ENV: z.string().optional().default('production'),
  APP_VERSION: z.string().optional(),

  LOG_LEVEL: z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace']).optional(),
  MEMORY_OBSERVABILITY: z.enum(['true', 'false']).optional(),

  SESSION_MAX_AGE: z.string().optional(),
  SESSION_IDLE_TIMEOUT: z.string().optional(),
  MARBLE_API_URL: z.string(),

  METABASE_URL: z.string().optional(),

  TEST_FIREBASE_AUTH_EMULATOR_HOST: z.string().optional(),

  SENTRY_DSN: z.string().optional(),
  SENTRY_ENVIRONMENT: z.string().optional(),

  SEGMENT_WRITE_KEY: z.string().optional(),
  DISABLE_SEGMENT: z
    .string()
    .transform((val) => val === 'true')
    .optional(),

  ENABLE_GRAPH_EXPLORATION: z.string().optional(),
  AI_RULE_CATALOG_ENABLED: z.string().optional(),

  // Probo cookie consent banner. When both are set, the banner is rendered and
  // Segment only loads after the visitor grants the "analytics" category.
  // An unset CI build arg becomes an empty Docker ENV value; normalize it to unset.
  PROBO_BANNER_ID: z.preprocess(
    (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
    z.string().trim().min(1).optional(),
  ),
  // Empty means unset; the banner fetches in the browser, so only HTTPS endpoints are valid.
  PROBO_BANNER_BASE_URL: z.preprocess(
    (val) => (val === '' ? undefined : val),
    z
      .url()
      .refine((value) => new URL(value).protocol === 'https:', 'Must use HTTPS')
      .optional(),
  ),

  // White-labeling: custom logo URL for sidebar
  CUSTOM_LOGO_URL: z.string().optional(),
});
type PublicEnvVars = z.infer<typeof PublicEnvVarsSchema>;

/**
 * List of all secret env vars to defined on each deployed environments
 */
const SecretEnvVarsSchema = z.object({
  SESSION_SECRET: z.string(),
});
type SecretEnvVars = z.infer<typeof SecretEnvVarsSchema>;

const EnvVarsSchema = PublicEnvVarsSchema.merge(SecretEnvVarsSchema).superRefine((env, context) => {
  const hasBannerId = env.PROBO_BANNER_ID !== undefined;
  const hasBaseUrl = env.PROBO_BANNER_BASE_URL !== undefined;
  if (hasBannerId !== hasBaseUrl) {
    context.addIssue({
      code: 'custom',
      message: 'PROBO_BANNER_ID and PROBO_BANNER_BASE_URL must be configured together',
      path: [hasBannerId ? 'PROBO_BANNER_BASE_URL' : 'PROBO_BANNER_ID'],
    });
  }
});
type EnvVars = PublicEnvVars & SecretEnvVars;

function getEnv<K extends keyof EnvVars>(envVarName: K) {
  return process.env[envVarName] as EnvVars[K];
}

/**
 * Used to check that all env vars are defined according to the schema
 * This is called at the beginning of the server and is only used for improved DX
 */
export function checkEnv() {
  const result = EnvVarsSchema.safeParse(process.env);
  if (!result.success) {
    const tree = z.treeifyError(result.error);
    const entries: Array<[string, string[]]> = Object.entries(tree.properties ?? {}).map(
      ([key, value]) => [key, value.errors] as [string, string[]],
    );
    const formatted = entries.map(([key, messages]) => `\t- ${key}: ${messages.join(', ')}`).join('\n');

    throw new Error(`[MissingEnv] validation issues :\n${formatted}`);
  }
}

/**
 * Server env vars, access it using getServerEnv('MY_ENV_VAR')
 */
interface ServerEnvVars {
  ENV: string;
  NODE_ENV: string;
  APP_VERSION?: string;
  LOG_LEVEL?: string;
  MEMORY_OBSERVABILITY?: 'true' | 'false';
  SESSION_MAX_AGE?: string;
  SESSION_IDLE_TIMEOUT?: string;
  MARBLE_API_URL: string;
  METABASE_URL?: string;
  TEST_FIREBASE_AUTH_EMULATOR_HOST?: string;
  SENTRY_DSN?: string;
  SENTRY_ENVIRONMENT?: string;
  SEGMENT_WRITE_KEY?: string;
  DISABLE_SEGMENT?: boolean;
  ENABLE_GRAPH_EXPLORATION?: string;
  AI_RULE_CATALOG_ENABLED?: string;
  PROBO_BANNER_ID?: string;
  PROBO_BANNER_BASE_URL?: string;
  SESSION_SECRET: string;
  CUSTOM_LOGO_URL?: string;
}

/**
 * Used to access env vars inside loaders/actions code
 */
export function getServerEnv<K extends keyof ServerEnvVars>(serverEnvVarName: K) {
  return getEnv(serverEnvVarName) as ServerEnvVars[K];
}

/**
 * Browser env vars, access it using getClientEnv('MY_ENV_VAR')
 */
interface ClientEnvVars {
  ENV: string;
  SENTRY_DSN?: string;
  SENTRY_ENVIRONMENT?: string;
  METABASE_URL?: string;
  CUSTOM_LOGO_URL?: string;
  ENABLE_GRAPH_EXPLORATION?: string;
  AI_RULE_CATALOG_ENABLED?: string;
  PROBO_BANNER_ID?: string;
  PROBO_BANNER_BASE_URL?: string;
}
export function getClientEnvVars(): ClientEnvVars {
  return {
    ENV: getServerEnv('ENV'),
    SENTRY_DSN: getServerEnv('SENTRY_DSN'),
    SENTRY_ENVIRONMENT: getServerEnv('SENTRY_ENVIRONMENT'),
    METABASE_URL: getServerEnv('METABASE_URL'),
    CUSTOM_LOGO_URL: getServerEnv('CUSTOM_LOGO_URL'),
    ENABLE_GRAPH_EXPLORATION: getServerEnv('ENABLE_GRAPH_EXPLORATION'),
    AI_RULE_CATALOG_ENABLED: getServerEnv('AI_RULE_CATALOG_ENABLED'),
    PROBO_BANNER_ID: getServerEnv('PROBO_BANNER_ID'),
    PROBO_BANNER_BASE_URL: getServerEnv('PROBO_BANNER_BASE_URL'),
  };
}

/**
 * Used to access env vars inside components code (SSR and CSR)
 */
export function getClientEnv<K extends keyof ClientEnvVars>(clientEnvVarName: K) {
  let clientEnv: ClientEnvVars;
  if (typeof window === 'undefined') {
    clientEnv = getClientEnvVars();
  } else {
    //@ts-expect-error ENV is a custom global variable injected in root.tsx
    clientEnv = window.ENV as ClientEnvVars;
    if (clientEnv === undefined) {
      throw new Error(`[MissingEnv] window.ENV is not defined. Check the root.tsx loader`);
    }
  }

  return clientEnv[clientEnvVarName];
}

export function isFlagActive(flag: string | undefined, orgId: string) {
  return flag === 'all' || !!flag?.split(',').find((segment) => segment.trim() === orgId);
}
